import { host } from "@/utils/utils"
import React, { useState, useEffect } from "react"
import { XMarkIcon } from "@heroicons/react/24/solid"
import PlayerListItemInfo from "@/Pages/Game/PlayerListItemInfo"
import classNames from "classnames"

interface FullStatsProps {
  gameId: string
  onClose: () => void
}

const FullStats: React.FC<FullStatsProps> = ({ gameId, onClose }) => {
  const [stats, setStats] = useState<IFullGameStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchStats = async () => {
      if (!gameId) return

      setLoading(true)
      setError(null)

      try {
        const response = await fetch(`${host}/api/stats`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ gameid: gameId }),
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        setStats(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch stats")
        console.error("Error fetching stats:", err)
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [gameId])

  if (loading) {
    return (
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] h-[90vh] bg-black bg-opacity-90 border-2 border-gray-700 z-[100] p-8 flex items-center justify-center">
        <div className="text-2xl font-poppins text-white">Loading stats...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] h-[90vh] bg-black bg-opacity-90 border-2 border-gray-700 z-[100] p-8 flex items-center justify-center">
        <div className="text-red-500 text-xl font-poppins">Error: {error}</div>
      </div>
    )
  }

  if (!stats) {
    return (
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] h-[90vh] bg-black bg-opacity-90 border-2 border-gray-700 z-[100] p-8 flex items-center justify-center">
        <div className="text-gray-400 text-xl font-poppins">
          No stats available
        </div>
      </div>
    )
  }

  const formatMoney = (amount: number) => {
    return `$${amount.toLocaleString()}`
  }

  return (
    <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-black bg-opacity-90 border-2 border-gray-700 z-[100] p-8 overflow-y-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-poppins text-white">Game Statistics</h2>
        <button
          onClick={onClose}
          aria-label="Close"
          className="flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-lg text-white transition-colors hover:bg-white/10 hover:text-gray-300"
        >
          <XMarkIcon className="h-[24px] w-[24px]" />
        </button>
      </div>

      <div className="text-sm text-gray-400 text-center mb-6">
        Game ID: {stats.gameid}
      </div>

      <div className="grid gap-6">
        {stats.stats.map((playerStats, index) => {
          const gamePlayer = stats.game?.situation?.players.find(
            (p) => p.order === playerStats.playerOrder
          )
          const playerEmail = stats.game?.players.find(
            (email) => email === gamePlayer?.id
          )

          if (!gamePlayer || !playerEmail) return null

          // Create a simple user object from the email
          const user: IUser = {
            username: playerEmail.split("@")[0], // Use email prefix as username
            password: "",
            isVerified: true,
            email: playerEmail,
            otp: "",
            otpTries: 0,
            resentOtpTimes: 0,
            stats: { rating: 0, gamesPlayed: 0, gamesWon: 0 },
            role: "none",
            profileImage: "",
          }

          return (
            <div
              key={index}
              className="bg-gray-800 border border-gray-700 rounded-lg p-6"
            >
              <div className="flex justify-between items-center mb-4">
                <PlayerListItemInfo
                  player={gamePlayer}
                  user={user}
                  distinguishActivePlayer={false}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Cards Section */}
                <div className="bg-gray-700 bg-opacity-50 p-4 rounded-lg">
                  <h4 className="font-medium text-white border-b border-gray-600 pb-2 mb-3 font-poppins">
                    Cards
                  </h4>
                  <div className="text-sm space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Cards Played:</span>
                      <span className="font-medium text-white">
                        {playerStats.cardsPlayed}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Cards Gained:</span>
                      <span className="font-medium text-white">
                        {playerStats.cardsGained}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Properties Section */}
                <div className="bg-gray-700 bg-opacity-50 p-4 rounded-lg">
                  <h4 className="font-medium text-white border-b border-gray-600 pb-2 mb-3 font-poppins">
                    Properties
                  </h4>
                  <div className="text-sm space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Properties Bought:</span>
                      <span className="font-medium text-white">
                        {playerStats.propertiesBought}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Houses Built:</span>
                      <span className="font-medium text-white">
                        {playerStats.housesBuilt}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Houses Destroyed:</span>
                      <span className="font-medium text-white">
                        {playerStats.ownHousesDestroyed}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Money Section */}
                <div className="bg-gray-700 bg-opacity-50 p-4 rounded-lg">
                  <h4 className="font-medium text-white border-b border-gray-600 pb-2 mb-3 font-poppins">
                    Money
                  </h4>
                  <div className="text-sm space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Money Gained:</span>
                      <span className="font-medium text-green-400">
                        {formatMoney(playerStats.moneyGained)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Money Lost:</span>
                      <span className="font-medium text-red-400">
                        {formatMoney(playerStats.moneyLost)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Net Money:</span>
                      <span
                        className={classNames("font-medium", {
                          "text-green-400":
                            playerStats.moneyGained - playerStats.moneyLost >=
                            0,
                          "text-red-400":
                            playerStats.moneyGained - playerStats.moneyLost < 0,
                        })}
                      >
                        {formatMoney(
                          playerStats.moneyGained - playerStats.moneyLost
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Rent Section */}
                <div className="bg-gray-700 bg-opacity-50 p-4 rounded-lg">
                  <h4 className="font-medium text-white border-b border-gray-600 pb-2 mb-3 font-poppins">
                    Rent
                  </h4>
                  <div className="text-sm space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Rent Paid:</span>
                      <span className="font-medium text-red-400">
                        {formatMoney(playerStats.rentPaid)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Rent Gained:</span>
                      <span className="font-medium text-green-400">
                        {formatMoney(playerStats.rentGained)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Stock Exchange Section */}
                <div className="bg-gray-700 bg-opacity-50 p-4 rounded-lg">
                  <h4 className="font-medium text-white border-b border-gray-600 pb-2 mb-3 font-poppins">
                    Stock Exchange
                  </h4>
                  <div className="text-sm space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Invested:</span>
                      <span className="font-medium text-blue-400">
                        {formatMoney(playerStats.moneyInvestedInStockExchange)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Retrieved:</span>
                      <span className="font-medium text-green-400">
                        {formatMoney(
                          playerStats.moneyRetrievedFromStockExchange
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Earnings:</span>
                      <span className="font-medium text-green-400">
                        {formatMoney(playerStats.stockExchangeEarnings)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Other Activities */}
                <div className="bg-gray-700 bg-opacity-50 p-4 rounded-lg">
                  <h4 className="font-medium text-white border-b border-gray-600 pb-2 mb-3 font-poppins">
                    Other Activities
                  </h4>
                  <div className="text-sm space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Rounds Completed:</span>
                      <span className="font-medium text-white">
                        {playerStats.roundsCompleted}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">
                        Money Used in Auctions:
                      </span>
                      <span className="font-medium text-orange-400">
                        {formatMoney(playerStats.moneyUsedInAuctions)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">
                        Money Gained by Cards:
                      </span>
                      <span className="font-medium text-green-400">
                        {formatMoney(playerStats.moneyGainedByCards)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">
                        Money Lost by Cards:
                      </span>
                      <span className="font-medium text-red-400">
                        {formatMoney(playerStats.moneyLostByCards)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default FullStats
